import type { GVector } from '../physics/index.ts';
import type { RideSummary, RideTraceSample } from '../ride/runner.ts';

export type SimulatedIntensityLevel = 'gentle' | 'moderate' | 'thrilling' | 'intense' | 'extreme';

export type SimulatedIntensityMetric =
  'speed' | 'positive_vertical_g' | 'negative_vertical_g' | 'lateral_g' | 'longitudinal_g';

export interface SimulatedIntensitySummary {
  level: SimulatedIntensityLevel;
  label: string;
  message: string;
  score: number;
}

export interface AuthoritativeIntensitySummary extends SimulatedIntensitySummary {
  strongestMetric: SimulatedIntensityMetric;
  strongestDistanceM: number;
  strongestCarIndex: number;
  sustainedSeconds: number | null;
}

export interface IntensityThresholdBands {
  readonly noticeable: number;
  readonly intense: number;
  readonly extreme: number;
  readonly absurd: number;
}

export interface IntensityThresholdConfig {
  readonly speed: IntensityThresholdBands;
  readonly positiveVerticalG: IntensityThresholdBands;
  readonly negativeVerticalG: IntensityThresholdBands;
  readonly absoluteLateralG: IntensityThresholdBands;
  readonly absoluteLongitudinalG: IntensityThresholdBands;
}

const SUMMARIES: Record<SimulatedIntensityLevel, Omit<SimulatedIntensitySummary, 'score'>> = {
  gentle: {
    level: 'gentle',
    label: 'Gentle',
    message: 'A relaxed ride with mild speed and forces.',
  },
  moderate: {
    level: 'moderate',
    label: 'Moderate',
    message: 'A lively ride with a few stronger moments.',
  },
  thrilling: {
    level: 'thrilling',
    label: 'Thrilling',
    message: 'A fast, forceful ride built for thrill seekers.',
  },
  intense: {
    level: 'intense',
    label: 'Intense',
    message: 'A demanding ride with sustained high-intensity moments.',
  },
  extreme: {
    level: 'extreme',
    label: 'Extreme',
    message: 'An exceptionally forceful ride.',
  },
};

export const INTENSITY_RANK: Readonly<Record<SimulatedIntensityLevel, number>> = {
  gentle: 0,
  moderate: 1,
  thrilling: 2,
  intense: 3,
  extreme: 4,
};

const WEIGHTS: Readonly<Record<SimulatedIntensityMetric, number>> = {
  speed: 0.2,
  positive_vertical_g: 0.2,
  negative_vertical_g: 0.2,
  lateral_g: 0.25,
  longitudinal_g: 0.15,
};

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
}

function normalizeSpeed(speedMps: number): number {
  return clamp01((Math.max(0, speedMps) - 5) / 35) * 100;
}

function normalizePositiveG(verticalG: number): number {
  return clamp01((verticalG - 1) / 4) * 100;
}

function normalizeNegativeG(verticalG: number): number {
  return clamp01((1 - verticalG) / 2.5) * 100;
}

function normalizeLateralG(lateralG: number): number {
  return clamp01(Math.abs(lateralG) / 2) * 100;
}

function normalizeLongitudinalG(longitudinalG: number): number {
  return clamp01(Math.abs(longitudinalG) / 2) * 100;
}

function metricScores(sample: {
  readonly speed: number;
  readonly verticalG: number;
  readonly lateralG: number;
  readonly longitudinalG: number;
}): Readonly<Record<SimulatedIntensityMetric, number>> {
  return {
    speed: normalizeSpeed(sample.speed),
    positive_vertical_g: normalizePositiveG(sample.verticalG),
    negative_vertical_g: normalizeNegativeG(sample.verticalG),
    lateral_g: normalizeLateralG(sample.lateralG),
    longitudinal_g: normalizeLongitudinalG(sample.longitudinalG),
  };
}

function weightedScore(scores: Readonly<Record<SimulatedIntensityMetric, number>>): number {
  return (Object.keys(WEIGHTS) as SimulatedIntensityMetric[]).reduce(
    (total, metric) => total + scores[metric] * WEIGHTS[metric],
    0,
  );
}

function levelForScore(score: number): SimulatedIntensityLevel {
  if (score >= 80) return 'extreme';
  if (score >= 60) return 'intense';
  if (score >= 40) return 'thrilling';
  if (score >= 20) return 'moderate';
  return 'gentle';
}

export function intensitySummary(
  level: SimulatedIntensityLevel,
  score = INTENSITY_RANK[level] * 20,
): SimulatedIntensitySummary {
  return { ...SUMMARIES[level], score: Math.round(Math.max(0, Math.min(100, score))) };
}

export function promoteIntensity(level: SimulatedIntensityLevel): SimulatedIntensityLevel {
  return level === 'gentle'
    ? 'moderate'
    : level === 'moderate'
      ? 'thrilling'
      : level === 'thrilling'
        ? 'intense'
        : 'extreme';
}

/**
 * Live force-only estimate used for ride feedback and audio. The authoritative
 * result is calculated from the completed trace by summarizeRideIntensity.
 */
export function classifySimulatedIntensity(vector: GVector): SimulatedIntensitySummary {
  const score = simulatedIntensityScore(vector);
  return intensitySummary(levelForScore(score), score);
}

function percentile(values: readonly number[], ratio: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const position = clamp01(ratio) * (sorted.length - 1);
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return sorted[lower]!;
  const mix = position - lower;
  return sorted[lower]! * (1 - mix) + sorted[upper]! * mix;
}

function instantaneousTraceScore(sample: RideTraceSample): number {
  return weightedScore(metricScores(sample));
}

function longestSustainedSeconds(
  trace: RideSummary['trace'],
  predicate: (sample: RideSummary['trace'][number]) => boolean,
): number {
  let start: number | null = null;
  let longest = 0;
  for (const sample of trace) {
    if (predicate(sample)) {
      start ??= sample.time;
      longest = Math.max(longest, sample.time - start);
    } else start = null;
  }
  return longest;
}

export function summarizeRideIntensity(
  summary: RideSummary,
  _thresholds?: IntensityThresholdConfig,
): AuthoritativeIntensitySummary {
  const trace = summary.trace.length
    ? summary.trace
    : [
        {
          time: 0,
          distance: summary.topSpeed.distance,
          speed: summary.topSpeed.value,
          direction: 1 as const,
          verticalG: summary.maxVerticalG.value,
          lateralG: summary.maxAbsLateralG.value,
          longitudinalG: summary.maxAbsLongitudinalG.value,
          sectionType: null,
        },
      ];
  const representativeScores = {
    speed: normalizeSpeed(
      percentile(
        trace.map((sample) => sample.speed),
        0.95,
      ),
    ),
    positive_vertical_g: normalizePositiveG(
      percentile(
        trace.map((sample) => sample.verticalG),
        0.95,
      ),
    ),
    negative_vertical_g: normalizeNegativeG(
      percentile(
        trace.map((sample) => sample.verticalG),
        0.05,
      ),
    ),
    lateral_g: normalizeLateralG(
      percentile(
        trace.map((sample) => Math.abs(sample.lateralG)),
        0.95,
      ),
    ),
    longitudinal_g: normalizeLongitudinalG(
      percentile(
        trace.map((sample) => Math.abs(sample.longitudinalG)),
        0.95,
      ),
    ),
  } satisfies Record<SimulatedIntensityMetric, number>;
  const baseScore = weightedScore(representativeScores);
  const sustainedSeconds = longestSustainedSeconds(
    trace,
    (sample) => instantaneousTraceScore(sample) >= 60,
  );
  const sustainedBonus = clamp01(sustainedSeconds / 5) * 10;
  const score = Math.round(Math.min(100, baseScore * 0.9 + sustainedBonus));

  let strongestSample = trace[0]!;
  let strongestScore = instantaneousTraceScore(strongestSample);
  for (const sample of trace.slice(1)) {
    const candidateScore = instantaneousTraceScore(sample);
    if (candidateScore > strongestScore) {
      strongestSample = sample;
      strongestScore = candidateScore;
    }
  }
  const strongestScores = metricScores(strongestSample);
  const strongestMetric = (Object.keys(WEIGHTS) as SimulatedIntensityMetric[]).reduce(
    (strongest, metric) =>
      strongestScores[metric] * WEIGHTS[metric] > strongestScores[strongest] * WEIGHTS[strongest]
        ? metric
        : strongest,
    'speed',
  );
  const level = levelForScore(score);
  return {
    ...intensitySummary(level, score),
    strongestMetric,
    strongestDistanceM: strongestSample.distance,
    strongestCarIndex: 0,
    sustainedSeconds: sustainedSeconds >= 0.5 ? sustainedSeconds : null,
  };
}

export function simulatedIntensityScore(vector: GVector): number {
  return weightedScore(
    metricScores({
      speed: 0,
      verticalG: vector.vertical,
      lateralG: vector.lateral,
      longitudinalG: vector.longitudinal,
    }),
  );
}
